import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Global Keyboard Shortcuts Hook
 * 
 * Handles global keyboard shortcuts across the application.
 * Context-aware: Only triggers when appropriate (no input/textarea focus).
 */
export const useKeyboardShortcuts = (options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    onOpenHelp,
    disabled = false
  } = options;

  useEffect(() => {
    if (disabled) return;

    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      const isInputFocused = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      
      // Allow ESC even in inputs (to close modals)
      if (e.key === 'Escape') {
        // ESC is handled by modal components directly
        return;
      }

      // Don't override browser defaults or interfere with typing
      if (isInputFocused && !['F1', 'F2', 'F3', 'F7', 'F8'].includes(e.key)) {
        return;
      }

      // F1: Help Modal
      if (e.key === 'F1') {
        e.preventDefault();
        if (onOpenHelp) {
          onOpenHelp();
        }
        return;
      }

      // F2: Navigate to Kasse (POS)
      if (e.key === 'F2') {
        e.preventDefault();
        if (location.pathname !== '/kassier') {
          navigate('/kassier');
        }
        return;
      }

      // F3: Navigate to Bestellübersicht
      if (e.key === 'F3') {
        e.preventDefault();
        if (location.pathname !== '/bestelluebersicht') {
          navigate('/bestelluebersicht');
        }
        return;
      }

      // Note: F5 is intentionally NOT prevented (browser reload)
      // Note: Ctrl+F / Cmd+F is intentionally NOT prevented (browser search)
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, location, onOpenHelp, disabled]);
};
